import { useMemo } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAsk } from '@/hooks/useAsk';
import { AskBox } from '@/components/ask/AskBox';
import { StreamingAnswer } from '@/components/ask/StreamingAnswer';
import { SourcesList } from '@/components/ask/SourcesList';
import { AskDisclaimer } from '@/components/ask/AskDisclaimer';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils/cn';

const SAMPLE_QUESTIONS = [
  'What does the Quran say about patience?',
  'How does the Quran describe gratitude?',
  'What verses speak about hardship and hope?',
];

const CITATION_PATTERN = /\((\d{1,3}):(\d{1,3})(?:-(\d{1,3}))?\)/g;

export function Ask() {
  useDocumentTitle('Ask');
  const ask = useAsk();

  /* Set of refs the model cited (used to dim non-cited sources). */
  const citedRefs = useMemo(() => {
    const set = new Set<string>();
    for (const m of ask.answer.matchAll(CITATION_PATTERN)) {
      const start = Number(m[2]);
      const end = m[3] ? Number(m[3]) : start;
      for (let v = start; v <= end; v++) set.add(`${m[1]}:${v}`);
    }
    return set;
  }, [ask.answer]);

  const isStreaming = ask.status === 'streaming';
  const showResult = ask.status !== 'idle' && ask.question;

  return (
    <section className="container-page pt-10 sm:pt-14 pb-16">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-3">
          Ask · Study Companion
        </p>
        <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
          Ask the Quran
        </h1>
        <p className="text-base text-ink-600 dark:text-ink-300 mt-3 max-w-prose leading-relaxed">
          A study tool grounded in the text. Every claim cites verses you can
          read.
        </p>
      </header>

      <div className="space-y-5 mb-10">
        <AskBox
          isStreaming={isStreaming}
          onSubmit={(q) => ask.ask(q)}
          onCancel={ask.cancel}
        />

        {!showResult && (
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-ink-400 dark:text-ink-500 mb-3">
              Try
            </p>
            <ul className="flex flex-wrap gap-2">
              {SAMPLE_QUESTIONS.map((q) => (
                <li key={q}>
                  <button
                    type="button"
                    onClick={() => ask.ask(q)}
                    className={cn(
                      'inline-flex items-center px-3 h-8 rounded-full text-sm',
                      'bg-ink-100 dark:bg-ink-900/70 hover:bg-ink-200 dark:hover:bg-ink-800',
                      'text-ink-700 dark:text-ink-200 transition-colors',
                    )}
                  >
                    {q}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showResult && (
        <div className="border-t border-ink-200/60 dark:border-ink-800/60 pt-10">
          <p className="text-xs uppercase tracking-[0.22em] text-ink-500 dark:text-ink-400 mb-3">
            Answer
          </p>

          {ask.status === 'error' ? (
            <EmptyState
              tone="error"
              title={errorTitle(ask.error?.code)}
              description={ask.error?.message ?? undefined}
              onRetry={() => ask.ask(ask.question)}
            />
          ) : (
            <>
              {ask.answer === '' && isStreaming && (
                <ThinkingHint />
              )}
              <StreamingAnswer
                text={ask.answer}
                invalidCitations={ask.invalidCitations}
                isStreaming={isStreaming}
              />
              {ask.invalidCitations.length > 0 && ask.status === 'done' && (
                <p className="mt-4 text-xs text-ink-500 dark:text-ink-400">
                  Note: {ask.invalidCitations.length} citation
                  {ask.invalidCitations.length === 1 ? '' : 's'} could not be
                  verified against the retrieved verses and {ask.invalidCitations.length === 1 ? 'has' : 'have'} been
                  marked.
                </p>
              )}
              <SourcesList sources={ask.sources} citedRefs={citedRefs} />
            </>
          )}
        </div>
      )}

      <footer className="mt-16">
        <AskDisclaimer />
      </footer>
    </section>
  );
}

function ThinkingHint() {
  return (
    <p
      aria-live="polite"
      className="text-sm text-ink-500 dark:text-ink-400 italic"
    >
      Thinking…
    </p>
  );
}

function errorTitle(code: string | undefined): string {
  switch (code) {
    case 'rate_limited':
      return 'You’ve reached the hourly limit.';
    case 'cost_capped':
      return 'Daily quota reached.';
    case 'bad_request':
      return 'That question can’t be processed.';
    case 'timeout':
      return 'Generation took too long.';
    default:
      return 'Something went wrong.';
  }
}
